import React, { useState, useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import Checkbox from '@material-ui/core/Checkbox';
import Shimmer from 'react-shimmer-effect';

import iconSet from '../../shared/images/teambuildr-selection.json';
import {
  fetchExercises,
  setExercises,
  setVBTDashView,
} from './ducks/vbtDashboardActions';
import Button from '../../shared/_TBComponents/Button';
import { dashboardTracker } from '../../shared/utils/amplitudeHelper';

const AvailableExercise = styled('div')`
  align-items: center;
  color: ${(props) => props.color};
  display: flex;
  justify-content: space-between;
  min-height: 50px;
  padding-left: 15px;
`;

const BackgroundContainer = styled('div')`
  background: white;
  border-radius: 8px;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const BoxTitle = styled('div')`
  display: flex;
  font-family: 'Nunito Sans';
  font-size: 16px;
  height: 25px;
  width: 100%;
`;

const CenterContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 85%;
`;

const ExercisesBox = styled('div')`
  background: #FCFCFC;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-top: 20px;
  overflow-y: scroll;
  padding: 15px;
  scrollbar-color: lightgrey transparent;
  scrollbar-width: thin;
`;

const Header = styled('div')`
  color: black;
  font-family: 'Nunito Sans';
  font-size: 23px;
  margin-top: 10px;
`;

const LeftBox = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 48%;
`;

const LowerBox = styled('div')`
  display: flex;
  height: 425px;
  justify-content: space-between;
  margin-top: 32px;
  width: 100%;
`;

const NewContainer = styled('div')`
  display: flex;
  height: auto;
  padding: 20px;
  width: 100%;
`;

const PlusIconContainer = styled('div')`
  align-items: center;
  border: 1px solid black;
  border-radius: 50%;
  display: flex;
  height: 75px;
  justify-content: center;
  width: 75px;
`;

const RemoveContainer = styled('div')`
  cursor: pointer;
`;

const RightBox = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 48%;
`;

const SearchAndFilter = styled('div')`
  display: flex;
  margin-top: 10px;
  width: 100%;
`;

const SearchAndIcon = styled('div')`
  align-items: center;
  background: white;
  border: 1px solid #EEEEEE;
  border-radius: 50px;
  display: flex;
  overflow: hidden;
  transition: max-width 0.5s ease;
  width: 100%;
`;

const SearchContainer = styled('input')`
  background: none;
  border: none;
  display: flex;
  height: 40px;
  padding-left: 5px;
  width: 100%;
`;

const SearchIconContainer = styled('div')`
  align-items: center;
  display: flex;
  justify-content: center;
  padding-left: 10px;
  padding-right: 5px;
`;

const SubHeader = styled('div')`
  color: ${(props) => props.color};
  font-family: 'Nunito Sans';
  font-size: 16px;
`;

const SubHeaderContainer = styled('div')`
  color: black;
  display: flex;
  font-family: 'Nunito Sans';
  font-size: 16px;
  margin-bottom: 16px;
  margin-top: 20px;
  width: 100%;
`;

const UpperContainer = styled('div')`
  align-items: center;
  border-bottom: 2px solid #EEEEEE;
  display: flex;
  flex-direction: column;
  height: 200px;
  justify-content: center;
  width: 100%;
`;

const NewVBTDashboard = ({
  setIsOverview,
  setNewDash,
}) => {
  const availableExercises = useSelector((state) => state.vbtDashboard.data.availableExercises);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const exercises = useSelector((state) => state.vbtDashboard.data.exercises);
  const isLoadingExercises = useSelector((state) => state.vbtDashboard.ui.isLoadingExercises);

  const dispatch = useDispatch();
  const theme = useTheme();

  const [searchedList, setSearchedList] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [areChecksDisabled, setAreChecksDisabled] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExercises, setSelectedExercises] = useState({});

  // Select exercises from redux if user navigated back to this page
  useEffect(() => {
    if (exercises?.length > 0) {
      const exercisesMap = {};
      exercises.forEach((exer) => {
        exercisesMap[exer.id] = exer;
      });
      setSelectedExercises(exercisesMap);
    }
  }, [exercises]);

  useEffect(() => {
    const searchedArray = availableExercises.filter(
      (exercise) => exercise.name.toLowerCase().includes(searchTerm.trim().toLowerCase()),
    );

    setSearchedList(searchedArray);
  }, [searchTerm, availableExercises]);

  useEffect(() => {
    const exerciseCount = Object.values(selectedExercises).filter(
      (value) => Object.keys(value).length > 0,
    ).length;

    setAreChecksDisabled(exerciseCount >= 10);
  }, [selectedExercises]);

  useEffect(() => {
    if (Object.keys(currentUser).length) {
      dispatch(fetchExercises(currentUser.accountCode));
    }
  }, [currentUser]);

  const exerciseResolver = (exercise) => {
    if (Object.keys(exercise).length) {
      return (
        <AvailableExercise color={theme.colors.text} key={exercise.id}>
          <div>{exercise.name}</div>
          <RemoveContainer onClick={() => {
            const newOb = { ...selectedExercises };
            delete newOb[exercise.id];
            setSelectedExercises(newOb);
          }}
          >
            <IcomoonReact
              iconSet={iconSet}
              size={15}
              icon='remove'
              color='#9E9E9E'
            />
          </RemoveContainer>
        </AvailableExercise>
      );
    }
    return null;
  };

  const isLoadDisabled = (selectedExercisesObject) => !Object.values(
    selectedExercisesObject,
  ).some(
    (exercise) => Object.keys(exercise).length > 0,
  );

  const submitExercises = (selectedExercisesObject) => {
    const submissionArray = Object.values(selectedExercisesObject)
      .filter((exercise) => Object.keys(exercise).length)
      .map(({
        id, name,
      }) => ({
        id,
        name,
      }));

    batch(() => {
      dispatch(setExercises(submissionArray));
      dispatch(setNewDash(false));
      setIsOverview();
      setVBTDashView('overview');
    });
  };

  return (
    <NewContainer>
      <BackgroundContainer>
        <CenterContainer>
          <UpperContainer>
            <PlusIconContainer>
              <IcomoonReact
                iconSet={iconSet}
                size={25}
                icon='plus'
                color='black'
              />
            </PlusIconContainer>
            <Header>
              New Dashboard
            </Header>
          </UpperContainer>
          <SubHeaderContainer>
            <SubHeader color={theme.colors.darkGray}>
              Select up to 10 exercises to add to this dashboard.
            </SubHeader>
          </SubHeaderContainer>
          <LowerBox>
            <LeftBox>
              <BoxTitle>
                All Exercises
              </BoxTitle>
              <SearchAndFilter>
                <SearchAndIcon id='search-container' onClick={() => setIsSearchFocused(true)} isFocused={isSearchFocused}>
                  <SearchIconContainer id='search-icon-container'>
                    <IcomoonReact
                      iconSet={iconSet}
                      size={20}
                      icon='magnifying-glass-alt'
                      color='#9E9E9E'
                    />
                  </SearchIconContainer>
                  <SearchContainer
                    placeholder='Search'
                    id='search-container-input'
                    value={searchTerm}
                    onFocus={() => setIsSearchFocused(true)}
                    onChange={(inputValue) => {
                      setSearchTerm(inputValue.target.value);
                    }}
                  />
                </SearchAndIcon>
              </SearchAndFilter>
              <ExercisesBox>
                {isLoadingExercises ? (
                  <Shimmer>
                    <AvailableExercise />
                  </Shimmer>
                ) : null}
                {!searchTerm.length ? availableExercises.map(
                  (exercise) => (
                    <AvailableExercise color={theme.colors.text} key={exercise.id}>
                      <div>{exercise.name}</div>
                      <div>
                        <Checkbox
                          checked={
                            !!(selectedExercises[exercise.id]
                            && Object.keys(selectedExercises[exercise.id]).length)
                          }
                          color='primary'
                          disabled={
                            areChecksDisabled
                            && (!selectedExercises[exercise.id]
                              || Object.keys(selectedExercises[exercise.id]).length === 0)
                          }
                          onChange={() => {
                            const newOb = { ...selectedExercises };
                            if (!newOb[exercise.id]) {
                              newOb[exercise.id] = exercise;
                            } else if (
                              newOb[exercise.id] && Object.keys(newOb[exercise.id]).length
                            ) {
                              newOb[exercise.id] = {};
                            } else if (
                              newOb[exercise.id] && Object.keys(newOb[exercise.id]).length === 0) {
                              newOb[exercise.id] = exercise;
                            }
                            (setSelectedExercises(newOb));
                          }}
                        />
                      </div>
                    </AvailableExercise>
                  ),
                ) : searchedList.map(
                  (exercise) => (
                    <AvailableExercise color={theme.colors.text} key={exercise.id}>
                      <div>{exercise.name}</div>
                      <div>
                        <Checkbox
                          checked={
                            !!(selectedExercises[exercise.id]
                            && Object.keys(selectedExercises[exercise.id]).length)
                          }
                          color='primary'
                          disabled={
                            areChecksDisabled
                            && (!selectedExercises[exercise.id]
                              || Object.keys(selectedExercises[exercise.id]).length === 0)
                          }
                          onChange={() => {
                            const newOb = { ...selectedExercises };
                            if (!newOb[exercise.id]) {
                              newOb[exercise.id] = exercise;
                            } else if (
                              newOb[exercise.id] && Object.keys(newOb[exercise.id]).length
                            ) {
                              newOb[exercise.id] = {};
                            } else if (
                              newOb[exercise.id] && Object.keys(newOb[exercise.id]).length === 0) {
                              newOb[exercise.id] = exercise;
                            }
                            (setSelectedExercises(newOb));
                          }}
                        />
                      </div>
                    </AvailableExercise>
                  ),
                )}
              </ExercisesBox>
            </LeftBox>
            <RightBox>
              <BoxTitle>
                Selected Exercises
              </BoxTitle>
              <ExercisesBox>
                {Object.values(selectedExercises).filter((e) => Object.keys(e).length)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((exercise) => exerciseResolver(exercise))}
              </ExercisesBox>
            </RightBox>
          </LowerBox>
          <Button
            bottom
            fullWidth
            customBorderRadius='4px'
            name='Load Dashboard'
            txtColor='#FFFFFF'
            upperCase
            large
            disabled={isLoadDisabled(selectedExercises)}
            noBorder
            bgColor='#444444'
            styledTextProps={{ fontSize: '14px' }}
            styledWrapperProps={{ margin: '20px 0' }}
            onClick={() => {
              submitExercises(selectedExercises);
              // Amplitude tracking
              dashboardTracker('VBT Dashboard', 'New Dashboard', 'Load dashboard clicked');
            }}
          />
        </CenterContainer>
      </BackgroundContainer>
    </NewContainer>
  );
};

NewVBTDashboard.propTypes = {
  setIsOverview: PropTypes.func.isRequired,
  setNewDash: PropTypes.func.isRequired,
};

export default NewVBTDashboard;
